import { camelCaseKeys, storageRepository } from '@betgames/bg-tools';
import { sessionStore } from '@bg-shared/business/Session';
import { API_URLS, NOTIFICATION_ERROR_SHOW_TIME } from '@bg-shared/constants';
import { httpClientRepository } from '@bg-shared/infrastructure/HttpClient';
import { IHowToPlayModel } from '@bg-shared/business/HowToPlay';
import { GameMessagePlacement, GameMessageType } from '@bg-shared/business/GamesMessages/enums';
import { parseGamificationError } from '@bg-shared/utils';
import { gamesMessagesEntity } from '@bg-shared/business/GamesMessages';
import {
    IGamificationProfile,
    IGamificationAvatars,
    IGamificationSettings,
    IGamificationBadge,
    IGamificationInbox,
    IGamificationLeaderboardPlayer,
    IGamificationAsset,
    IAssetsPurchaseHistory,
    GamificationErrorType,
} from '../interfaces';
import { gamificationStore } from '../store/Gamification.store';
import { STORAGE_KEY, CAPTAIN_UP_LANGUAGE_CONFIG } from '../constants';
import { captainUpAPI } from './CaptainUpAPI';
import { inboxesFormatter, badgesFormatter, tournamentsFormatter } from '../utils';
import { gamificationEvents } from './Gamification.events';

class GamificationService {
    // TODO: start interval only when modal is opened and logged in?
    // private readonly REFRESH_DATA_INTERVAL = 30000; // 30s

    private showError = (error: GamificationErrorType) => {
        gamesMessagesEntity.showMessage(
            {
                type: GameMessageType.DANGER,
                message: parseGamificationError(error),
                timeout: NOTIFICATION_ERROR_SHOW_TIME,
            },
            GameMessagePlacement.TOP,
        );
    };

    private getBadges(): Promise<Record<string, IGamificationBadge>> {
        return captainUpAPI
            .getBadges()
            .then((badges) => badgesFormatter.formatBadges(badges))
            .catch((error) => {
                this.showError(error);
                return {};
            });
    }

    private getAssets(): Promise<IGamificationAsset[]> {
        return captainUpAPI
            .getAssets()
            .then((response) => {
                return camelCaseKeys(response) as unknown as IGamificationAsset[];
            })
            .catch((error) => {
                this.showError(error);

                return [];
            });
    }

    private getAssetsPurchaseHistory(): Promise<IAssetsPurchaseHistory> {
        return httpClientRepository
            .get<{
                purchasesByHashes: IAssetsPurchaseHistory;
            }>(API_URLS.GAMIFICATION.PURCHASE_HISTORY, {
                transformResponse: false,
            })
            .then((response) => response.purchasesByHashes)
            .catch((error) => {
                this.showError(error);
                return {};
            });
    }

    public createUser(name: string): Promise<IGamificationProfile> {
        return httpClientRepository
            .post<IGamificationProfile>(API_URLS.GAMIFICATION.CREATE_PROFILE, {
                data: { name },
            })
            .catch((error) => {
                this.showError(error);
                throw error;
            });
    }

    public updateProfile(image: string): Promise<IGamificationProfile | void> {
        return httpClientRepository
            .post<IGamificationProfile>(API_URLS.GAMIFICATION.UPDATE_PROFILE, {
                data: { avatar: image },
                transformRequest: false,
            })
            .catch((error) => {
                this.showError(error);
                throw error;
            });
    }

    public blockProfile(reason: string): Promise<IGamificationProfile> {
        return httpClientRepository
            .post<IGamificationProfile>(API_URLS.GAMIFICATION.BLOCK_PROFILE, {
                data: { reason },
            })
            .then((profile) => {
                gamificationStore.toggleProfileBlock(true);
                return profile;
            })
            .catch((error) => {
                this.showError(error);
                throw error;
            });
    }

    public unblockProfile(): Promise<IGamificationProfile> {
        return httpClientRepository
            .post<IGamificationProfile>(API_URLS.GAMIFICATION.UNBLOCK_PROFILE)
            .then((profile) => {
                gamificationStore.toggleProfileBlock(false);
                return profile;
            })
            .catch((error) => {
                this.showError(error);
                throw error;
            });
    }

    public removeProfile(reason: string): Promise<void> {
        return httpClientRepository
            .post(API_URLS.GAMIFICATION.DELETE_PROFILE, {
                data: { reason },
            })
            .then(() => {
                storageRepository.removeItem(STORAGE_KEY);
                gamificationStore.clearStore();
            })
            .catch((error) => {
                this.showError(error);
                throw error;
            });
    }

    public nicknameAvailable(nickname: string): Promise<{ isAvailable: boolean }> {
        return httpClientRepository.post<{ isAvailable: boolean }>(
            API_URLS.GAMIFICATION.NICKNAME_AVAILABLE,
            {
                data: {
                    name: nickname,
                },
            },
        );
    }

    public getAvatars(): Promise<IGamificationAvatars> {
        return httpClientRepository.get<IGamificationAvatars>(API_URLS.GAMIFICATION.GET_AVATARS, {
            transformResponse: false,
        });
    }

    public configure(): void {
        httpClientRepository
            .get<IGamificationSettings>(API_URLS.GAMIFICATION.SETTINGS)
            .then((settings) => {
                if (!settings) {
                    throw Error('No settings');
                }

                gamificationStore.updateSettings(settings);

                if (settings.profile) {
                    this.init(settings.profile, sessionStore.partnerCode)
                        .then(() => {
                            gamificationStore.setSettingsLoaded();
                        })
                        .catch((error) => {
                            console.error(error);
                            this.showError(error);
                        });
                } else {
                    gamificationStore.setSettingsLoaded();
                }
            })
            .catch((error) => {
                console.error(`Error fetching gamification settings: ${error}`);
                this.showError(error);
            });
    }

    public inboxToggle(inbox: IGamificationInbox): void {
        if (!inbox.unread) {
            gamificationStore.toggleInboxExpand(inbox);
            return;
        }

        captainUpAPI
            .markAsRead(inbox.id)
            .then((response) => {
                const updatedInbox = {
                    ...inbox,
                    unread: false,
                    unseen: false,
                    expanded: !inbox.expanded,
                };

                const currentInboxes = gamificationStore.value.inboxes;
                const updatedInboxes = [...currentInboxes];
                const inboxIndex = updatedInboxes.findIndex((item) => item.id === inbox.id);

                if (inboxIndex !== -1) {
                    updatedInboxes[inboxIndex] = updatedInbox;
                }

                const mergedInboxes = inboxesFormatter.mergeInboxes(
                    updatedInboxes,
                    inboxesFormatter.formatInboxes(response.data),
                );

                gamificationStore.updateInboxes(mergedInboxes);
            })
            .catch((error) => {
                this.showError(error);
            });
    }

    public updateChallenges(): void {
        this.getBadges().then((badges) => {
            gamificationStore.updateChallenges(
                badgesFormatter.formatChallenges(
                    badges,
                    sessionStore.timezone,
                    gamificationStore.value.badges.challenges,
                    null,
                ),
            );
        });
    }

    public updateTournaments(): void {
        captainUpAPI
            .getTournaments()
            .then((tournaments) => {
                gamificationStore.updateTournaments(
                    tournamentsFormatter.formatTournaments(tournaments),
                );
            })
            .catch((error) => {
                this.showError(error);
            });
    }

    public toggleTournamentParticipation(tournamentId: string, toggle: boolean): Promise<void> {
        if (toggle) {
            return captainUpAPI
                .registerToTournament(tournamentId)
                .then(() => gamificationStore.toggleTournamentParticipation(tournamentId, true))
                .catch((error) => {
                    throw error;
                });
        }

        return captainUpAPI
            .unregisterFromTournament(tournamentId)
            .then(() => gamificationStore.toggleTournamentParticipation(tournamentId, false))
            .catch((error) => {
                throw error;
            });
    }

    public getPlayerTournamentData(
        tournamentId: string,
    ): Promise<IGamificationLeaderboardPlayer | null | void> {
        return captainUpAPI
            .getMyTournamentStats(tournamentId)
            .then((data) =>
                tournamentsFormatter
                    .formatLeaderboards(data)
                    .find((item) => item.name === gamificationStore.value.profile.userInfo.name),
            )
            .catch((error) => {
                this.showError(error);
            });
    }

    public getTournamentLeaderboard(
        tournamentId: string,
        batchSize: number,
        skip = 0,
    ): Promise<{ list: IGamificationLeaderboardPlayer[]; total: number } | null | void> {
        return captainUpAPI
            .getTournamentLeaderboard({
                id: tournamentId,
                batchSize,
                skip,
            })
            .then((data) => ({
                list: tournamentsFormatter.formatLeaderboards(data.list),
                total: data.total,
            }))
            .catch((error) => {
                this.showError(error);
            });
    }

    public async init(profile: IGamificationProfile, partnerCode: string): Promise<void> {
        const state = gamificationStore.value;

        if (state.profile.blocked) {
            return;
        }

        try {
            const trimmedLocale = sessionStore.language.replace(/\d+$/, '');
            const sliced = sessionStore.language.slice(0, 2);

            await captainUpAPI.setupSDK(state.settings.sdkUrl, {
                api_key: state.settings.appKey,
                signed_user: profile.signedUser,
                client_token: state.settings.clientToken,
                lang:
                    CAPTAIN_UP_LANGUAGE_CONFIG[trimmedLocale] ||
                    CAPTAIN_UP_LANGUAGE_CONFIG[sliced] ||
                    'en-us',
                inboxSync: true,
                user: {
                    id: profile.id,
                    name: profile.name,
                    partner_code: `${state.settings.envPrefix}:${partnerCode}`,
                    image: profile.image,
                },
            });

            gamificationStore.setSDKLoaded();
        } catch (error) {
            this.showError(error);
            console.error(`Error configuring gamification SDK ${error}`);
            return;
        }

        try {
            const lastSeenBadges = storageRepository.getItemParsed<string[]>(STORAGE_KEY);
            const [user, level, nextLevel, tournaments, badges] = await Promise.all([
                captainUpAPI.getUser(),
                captainUpAPI.getLevel(),
                captainUpAPI.getNextLevel(),
                captainUpAPI.getTournaments(),
                this.getBadges(),
            ]);

            const unreadInboxes = inboxesFormatter.formatInboxes(user.unread_inbox);
            const inboxes = inboxesFormatter.mergeInboxes(
                inboxesFormatter.formatInboxes(user.inbox_activities),
                unreadInboxes,
            );
            const challengesBadges = badgesFormatter.formatChallenges(
                badges,
                sessionStore.timezone,
                null,
                lastSeenBadges || [],
            );
            const unreadBadgedActivities = badgesFormatter.formatActivities(user.unread_activities);
            const achievedBadges = badgesFormatter.formatAchievedChallenges(
                user.achieved_badges,
                badges,
                unreadBadgedActivities,
                sessionStore.timezone,
            );
            const shownNotifications = [
                ...unreadInboxes,
                ...achievedBadges.filter((item) => item.unread),
            ].map((inbox) => inbox.id);
            const tournamentsFormatted = {
                list: tournamentsFormatter.formatTournaments(tournaments),
                optInIds: user.currently_active_and_registered_tournaments,
            };

            gamificationStore.setInitialData({
                badges: {
                    achieved: achievedBadges,
                    challenges: challengesBadges,
                },
                inboxes,
                notifications: {
                    shownNotifications,
                    notificationMessage: null,
                },
                profile: {
                    userInfo: {
                        id: profile.id,
                        name: profile.name,
                        signedUser: profile.signedUser,
                        image: user.image,
                        points: user.currencies.points,
                        coins: user.currencies.coins,
                    },

                    level: {
                        number: level.number,
                        previousLevel: level.number,
                        nextLevelPoints: nextLevel.points,
                        points: level.points,
                    },
                    blocked: false,
                },
                tournaments: tournamentsFormatted,
            });

            storageRepository.setItemParsed(
                STORAGE_KEY,
                challengesBadges.map((badge) => badge.id),
            );

            gamificationEvents.configure();
        } catch (error) {
            this.showError(error);
            console.error(`Error configuring gamification ${error}`);
        }
    }

    public purchaseAsset(id: string): Promise<void | boolean> {
        return httpClientRepository
            .post(API_URLS.GAMIFICATION.PURCHASE_ASSET, {
                data: {
                    assetHash: id,
                },
                transformRequest: true,
            })
            .then(() => this.getAssetsPurchaseHistory())
            .then((history) => {
                if (Object.values(history).length) {
                    gamificationStore.updateAssetsHistory(history);
                }

                return true;
            })
            .catch((error) => {
                this.showError(error);
                return false;
            });
    }

    public updateShopAssets(): void {
        Promise.all([this.getAssets(), this.getAssetsPurchaseHistory()]).then(
            ([assets, history]) => {
                gamificationStore.updateShopAssets(assets, history);
            },
        );
    }

    public getIntroPageTranslations(): Promise<IHowToPlayModel> {
        return httpClientRepository
            .get<IHowToPlayModel>(`${API_URLS.GAMIFICATION.HOW_TO_PLAY}/${sessionStore.language}`)
            .catch((error) => {
                this.showError(error);
                throw error;
            });
    }

    public updateIntroPageTranslations(): void {
        this.getIntroPageTranslations()
            .then((data) => gamificationStore.updateIntroPageTranslations(data))
            .catch(() => null);
    }
}

export const gamificationService = new GamificationService();
